import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { createLeadCapture } from "@/api";

export default function LeadCapture() {
  const { t, i18n } = useTranslation("common");
  const navigate = useNavigate();
  const isRTL = i18n.language === "ar";

  const [formData, setFormData] = useState({
    childName: "",
    dob: "",
    gender: "",
    parentPhone: "",
    parentEmail: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasAssessmentData, setHasAssessmentData] = useState(true);

  useEffect(() => {
    const stored = sessionStorage.getItem("assessmentData");
    if (!stored) {
      setHasAssessmentData(false);
    }
  }, []);

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.childName.trim()) newErrors.childName = t("requiredField");
    if (!formData.dob) newErrors.dob = t("requiredField");
    if (!formData.gender) newErrors.gender = t("requiredField");
    if (!formData.parentPhone.trim()) newErrors.parentPhone = t("requiredField");
    if (!formData.parentEmail.trim()) {
      newErrors.parentEmail = t("requiredField");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.parentEmail)) {
      newErrors.parentEmail = t("invalidEmail");
    }
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const stored = sessionStorage.getItem("assessmentData");
      if (!stored) {
        setHasAssessmentData(false);
        return;
      }

      const assessmentData = JSON.parse(stored) as {
        scores?: Record<string, number>;
      };

      const scores = assessmentData?.scores || {};

      const result = await createLeadCapture({
        parent: {
          name: "ولي الأمر",
          phoneNumber: formData.parentPhone,
          email: formData.parentEmail,
          password: "password123",
        },
        child: {
          name: formData.childName,
          dob: formData.dob,
          gender: formData.gender,
        },
        assessment: {
          scorePhysical: Number(scores.physical || 0),
          scoreCognitive: Number(scores.cognitive || 0),
          scoreSocial: Number(scores.social || 0),
        },
      });

      navigate(`/processing?reportId=${result.reportId}`);
    } catch (error) {
      setErrors({ form: t("dataLoadFailed") });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = (field: string) =>
    `w-full px-4 py-3 rounded-xl border-2 text-sm md:text-base transition-all duration-200 focus:outline-none ${
      errors[field]
        ? "border-red-300 bg-red-50 focus:border-red-400"
        : "border-gray-200 bg-white focus:border-primary-400 focus:ring-2 focus:ring-primary-400/10"
    }`;

  return (
    <div className="min-h-screen bg-[#FFFBF7] pt-20 md:pt-24 pb-8">
      <div className="w-full px-4 md:px-8 lg:px-16 max-w-xl mx-auto">
        {!hasAssessmentData && (
          <div className="mb-6 rounded-xl bg-red-50 text-red-500 px-4 py-3 text-sm">
            {t("dataLoadFailed")}
          </div>
        )}
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-primary-400/10 flex items-center justify-center mx-auto mb-4">
            <i className="ri-file-list-3-line text-primary-400 text-2xl" />
          </div>
          <h1 className="text-xl md:text-2xl font-bold text-navy-400 mb-2">{t("leadTitle")}</h1>
          <p className="text-sm md:text-base text-gray-500 leading-relaxed">{t("leadSubtitle")}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-5 md:p-8 border border-gray-100 shadow-sm">
          <div className="space-y-5">
            {/* Child Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t("childName")}</label>
              <input
                type="text"
                value={formData.childName}
                onChange={(e) => setFormData({ ...formData, childName: e.target.value })}
                placeholder={t("childNamePlaceholder")}
                className={inputClass("childName")}
              />
              {errors.childName && <p className="text-xs text-red-500 mt-1">{errors.childName}</p>}
            </div>

            {/* Date of Birth */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t("dob")}</label>
              <input
                type="date"
                value={formData.dob}
                onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                className={inputClass("dob")}
              />
              {errors.dob && <p className="text-xs text-red-500 mt-1">{errors.dob}</p>}
            </div>

            {/* Gender */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t("gender")}</label>
              <div className="flex gap-3">
                {[
                  { value: "male", label: t("male"), icon: "ri-men-line" },
                  { value: "female", label: t("female"), icon: "ri-women-line" },
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, gender: option.value })}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 transition-all ${
                      formData.gender === option.value
                        ? "border-primary-400 bg-primary-50 text-primary-500"
                        : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                    }`}
                  >
                    <i className={option.icon} />
                    <span className="text-sm font-medium">{option.label}</span>
                  </button>
                ))}
              </div>
              {errors.gender && <p className="text-xs text-red-500 mt-1">{errors.gender}</p>}
            </div>

            {/* Parent Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t("parentPhone")}</label>
              <input
                type="tel"
                value={formData.parentPhone}
                onChange={(e) => setFormData({ ...formData, parentPhone: e.target.value })}
                placeholder={t("phonePlaceholder")}
                className={inputClass("parentPhone")}
              />
              {errors.parentPhone && <p className="text-xs text-red-500 mt-1">{errors.parentPhone}</p>}
            </div>

            {/* Parent Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t("parentEmail")}</label>
              <input
                type="email"
                value={formData.parentEmail}
                onChange={(e) => setFormData({ ...formData, parentEmail: e.target.value })}
                placeholder={t("emailPlaceholder")}
                className={inputClass("parentEmail")}
              />
              {errors.parentEmail && <p className="text-xs text-red-500 mt-1">{errors.parentEmail}</p>}
            </div>
          </div>

          {/* Privacy Note */}
          <p className="text-xs text-gray-400 mt-5 text-center leading-relaxed">{t("privacyNote")}</p>

          {errors.form && <p className="text-xs text-red-500 mt-2 text-center">{errors.form}</p>}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full mt-6 px-8 py-4 bg-primary-400 hover:bg-primary-500 disabled:bg-gray-300 text-white rounded-full font-semibold text-base transition-all duration-300 flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t("processing")}
              </>
            ) : (
              <>
                {t("generateReport")}
                {isRTL ? <i className="ri-arrow-left-line" /> : <i className="ri-arrow-right-line" />}
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}